import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, Loader2, AlertCircle } from 'lucide-react';
import { isPersonalEmail, CORPORATE_EMAIL_MESSAGE } from '@/lib/corporateEmail';
import { getFunctionErrorMessage } from '@/lib/functionError';

const EMPLOYEE_RANGES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

export default function EnterpriseSignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    company_name: '',
    position: '',
    phone: '',
    sector: '',
    employee_count: '',
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleEmailBlur = () => {
    if (form.email && isPersonalEmail(form.email)) setEmailError(CORPORATE_EMAIL_MESSAGE);
    else setEmailError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isPersonalEmail(form.email)) {
      setEmailError(CORPORATE_EMAIL_MESSAGE);
      return;
    }
    if (form.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('register-enterprise', {
        body: form,
      });
      const payload = data as { error?: string; ok?: boolean } | null;
      if (error) {
        toast.error(
          await getFunctionErrorMessage(
            error,
            'No se pudo completar el registro. Intenta de nuevo.',
          ),
        );
        return;
      }
      if (payload?.error) {
        toast.error(payload.error);
        return;
      }

      // La cuenta nace con rol participante + admin: abrir en vista administrador.
      localStorage.setItem('lia_current_role', 'admin');

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      if (signInError) {
        toast.success('Empresa registrada. Inicia sesión para continuar.');
        navigate('/auth');
        return;
      }

      toast.success('¡Listo! Ya puedes responder el diagnóstico de tu empresa.');
      navigate('/enterprise-diagnostic');
    } catch (error) {
      toast.error(
        await getFunctionErrorMessage(
          error,
          'No se pudo completar el registro. Intenta de nuevo.',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6">
      <SEO
        title="Registra tu empresa — LÍDER IA"
        description="Crea la cuenta de tu empresa y mide su madurez en inteligencia artificial."
        path="/registro-empresa"
      />
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
            <Building2 className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>Registra tu empresa</CardTitle>
          <CardDescription>
            Cuenta gratuita para responder el Diagnóstico Empresarial de madurez en IA. Necesitas el correo
            de tu empresa.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Nombre completo *</Label>
                <Input
                  id="full_name"
                  required
                  value={form.full_name}
                  onChange={(e) => set('full_name')(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="position">Cargo *</Label>
                <Input
                  id="position"
                  required
                  value={form.position}
                  onChange={(e) => set('position')(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo corporativo *</Label>
              <Input
                id="email"
                type="email"
                required
                placeholder="nombre@tuempresa.com"
                value={form.email}
                onChange={(e) => {
                  set('email')(e.target.value);
                  if (emailError) setEmailError(null);
                }}
                onBlur={handleEmailBlur}
                aria-invalid={!!emailError}
              />
              {emailError && (
                <p className="text-sm text-destructive flex items-start gap-1.5">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  {emailError}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña *</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => set('password')(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">Nombre de la empresa *</Label>
              <Input
                id="company_name"
                required
                value={form.company_name}
                onChange={(e) => set('company_name')(e.target.value)}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sector">Sector</Label>
                <Input
                  id="sector"
                  value={form.sector}
                  onChange={(e) => set('sector')(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Número de empleados</Label>
                <Select value={form.employee_count} onValueChange={set('employee_count')}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_RANGES.map((r) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Celular</Label>
              <Input
                id="phone"
                placeholder="+57 300 000 0000"
                value={form.phone}
                onChange={(e) => set('phone')(e.target.value)}
              />
            </div>

            <Button type="submit" className="w-full gap-2" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Crear cuenta de empresa
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              ¿Ya tienes cuenta?{' '}
              <button type="button" className="text-primary underline" onClick={() => navigate('/auth')}>
                Inicia sesión
              </button>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
